/**
 * Booqable Cart Drawer - Custom Element
 * A high-performance, encapsulated Web Component for Booqable rental cart management
 * 
 * @element booqable-cart-drawer
 * @fires cart:open - Dispatched when drawer opens
 * @fires cart:close - Dispatched when drawer closes
 * @fires cart:update - Dispatched when cart is updated
 */

// Constants
const DEBOUNCE_DELAY = 500;
const POLL_INTERVAL = 1000;
const API_UPDATE_GRACE_PERIOD = 3000;
const SDK_TIMEOUT = 10000;

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
    GBP: '£',
    USD: '$',
    EUR: '€'
};

/**
 * BooqableCartDrawer Custom Element
 * Manages the cart drawer UI and integrates with Booqable API
 */
class BooqableCartDrawer extends HTMLElement {
    // Private fields
    #isInitialized = false;
    #cartData = null;
    #fullCartData = null;
    #apiBaseUrl = null;
    #storefrontToken = null;
    #lastApiUpdate = null;
    #debounceTimers = new Map();
    #pollInterval = null;
    #abortController = null;

    // DOM references
    #drawer = null;
    #overlay = null;
    #content = null;
    #footer = null;
    #minValueSlot = null;
    #minSpendModal = null;

    constructor() {
        super();
    }

    /**
     * Web Component lifecycle - called when element is added to DOM
     */
    async connectedCallback() {
        if (this.#isInitialized) return;

        try {
            await this.#initialize();
            this.#isInitialized = true;
        } catch (error) {
            console.error('[BooqableCartDrawer] Initialization failed:', error);
        }
    }

    /**
     * Web Component lifecycle - called when element is removed from DOM
     */
    disconnectedCallback() {
        this.#cleanup();
    }

    // ============================================
    // INITIALIZATION
    // ============================================

    /**
     * Initialize the cart drawer
     */
    async #initialize() {
        // Setup DOM references
        this.#drawer = this.querySelector('.BooqableDrawer') || this;
        this.#overlay = document.getElementById('booqable-drawer-overlay');
        this.#content = this.querySelector('.BooqableDrawer__Content');
        this.#footer = this.querySelector('.BooqableDrawer__Footer');
        this.#minValueSlot = this.querySelector('.BooqableDrawer__MinValue');
        this.#minSpendModal = document.getElementById('booqable-minspend-modal');

        if (!this.#content || !this.#footer) {
            throw new Error('Required drawer elements not found');
        }

        // Wait for Booqable SDK
        const booqable = await this.#waitForSDK();
        if (!booqable) {
            console.warn('[BooqableCartDrawer] SDK not available, limited functionality');
        }

        this.#cartData = booqable?.cartData || null;

        // Configure API
        const slug = window.booqableOptions?.slug || window.BooqableConfig?.slug || 'the-social-kitchen';
        this.#apiBaseUrl = window.BooqableConfig?.apiBaseUrl || `https://${slug}.booqableshop.com/api/1`;
        this.#storefrontToken = this.#extractStorefrontToken();

        // Bind events and render
        this.#bindEvents();
        await this.#fetchFullCartData();
        this.#render();
        this.#updateHeaderCount();
        this.#observeCartChanges();
    }

    /**
     * Cleanup resources
     */
    #cleanup() {
        // Clear debounce timers
        this.#debounceTimers.forEach((timer) => clearTimeout(timer));
        this.#debounceTimers.clear();

        // Clear poll interval
        if (this.#pollInterval) {
            clearInterval(this.#pollInterval);
            this.#pollInterval = null;
        }

        // Abort pending requests
        if (this.#abortController) {
            this.#abortController.abort();
            this.#abortController = null;
        }

        // // Remove SDK listeners
        // if (window.Booqable?.off) {
        //     window.Booqable.off('cart:updated');
        // }
    }

    /**
     * Wait for Booqable SDK to be ready
     */
    #waitForSDK() {
        return new Promise((resolve) => {
            const startTime = Date.now();

            const check = () => {
                if (window.Booqable?.cartData !== undefined) {
                    resolve(window.Booqable);
                    return;
                }

                if (Date.now() - startTime > SDK_TIMEOUT) {
                    resolve(null);
                    return;
                }

                requestAnimationFrame(check);
            };

            check();
        });
    }

    /**
     * Extract storefront token from page
     */
    #extractStorefrontToken() {
        // Check meta tag
        const meta = document.querySelector('meta[name="booqable-storefront-token"]');
        if (meta?.content) return meta.content;

        // Check booqableOptions
        if (window.booqableOptions?.storefrontToken) {
            return window.booqableOptions.storefrontToken;
        }

        // Check SDK scripts
        const scripts = document.querySelectorAll('script[src*="booqable"]');
        for (const script of scripts) {
            const match = script.src.match(/token=([a-f0-9]+)/);
            if (match) return match[1];
        }

        return window.BooqableConfig?.storefrontToken || '5e6ebf3d24ab26f8c300721e9ff8485e';
    }

    // ============================================
    // EVENT HANDLING
    // ============================================

    /**
     * Bind all event listeners using event delegation
     */
    #bindEvents() {
        // Open drawer trigger (external button)
        document.addEventListener('click', this.#handleGlobalClick.bind(this));

        // Close drawer
        this.querySelector('[data-action="close-booqable-drawer"]')
            ?.addEventListener('click', () => this.close());

        // Overlay click
        this.#overlay?.addEventListener('click', () => this.close());

        // Keyboard events
        document.addEventListener('keydown', this.#handleKeydown.bind(this));

        // Cart item actions (using event delegation)
        this.#content?.addEventListener('click', this.#handleContentClick.bind(this));
        this.#content?.addEventListener('change', this.#handleContentChange.bind(this));

        // Footer actions (date error click to open picker, below-minimum prompt)
        this.#footer?.addEventListener('click', this.#handleFooterClick.bind(this));

        // Sample-order checkbox (overrides the minimum spend)
        this.#footer?.addEventListener('change', this.#handleFooterChange.bind(this));

        // Below-minimum prompt close actions
        this.#minSpendModal
            ?.querySelectorAll('[data-action="close-minspend-modal"]')
            .forEach((el) => el.addEventListener('click', () => this.#closeMinSpendModal()));

        // Below-minimum prompt trigger under the progress bar
        this.#minValueSlot?.addEventListener('click', (event) => {
            if (event.target.closest('[data-action="open-minspend-modal"]')) {
                this.#openMinSpendModal();
            }
        });
    }

    /**
     * Handle global clicks (for open button)
     */
    #handleGlobalClick(event) {
        const trigger = event.target.closest('[data-action="open-booqable-cart"]');
        if (trigger) {
            event.preventDefault();
            this.open();
        }
    }

    /**
     * Handle keyboard events
     */
    #handleKeydown(event) {
        if (event.key !== 'Escape') return;

        if (this.#isMinSpendModalOpen) {
            this.#closeMinSpendModal();
            return;
        }

        if (this.isOpen) {
            this.close();
        }
    }

    /**
     * Handle clicks within cart content (event delegation)
     */
    #handleContentClick(event) {
        const target = event.target;

        // Quantity buttons
        const quantityBtn = target.closest('.BooqableCart__QuantityBtn');
        if (quantityBtn) {
            this.#handleQuantityButton(quantityBtn);
            return;
        }

        // Remove button
        const removeBtn = target.closest('[data-action="remove"]');
        if (removeBtn) {
            const index = parseInt(removeBtn.dataset.index, 10);
            this.#removeItem(index);
            return;
        }

        // Edit dates button
        const editDatesBtn = target.closest('[data-action="edit-dates"]');
        if (editDatesBtn) {
            this.#handleEditDates();
            return;
        }
    }

    /**
     * Handle clicks within footer (date error click)
     */
    #handleFooterClick(event) {
        const target = event.target;

        // Below-minimum carts: intercept Continue and show the "get in touch" prompt
        const checkoutBtn = target.closest('.BooqableCart__CheckoutBtn');
        if (checkoutBtn?.dataset.minNotMet === 'true') {
            event.preventDefault();
            this.#openMinSpendModal();
            return;
        }

        // Open date picker from date error message
        const dateErrorClick = target.closest('[data-action="open-date-picker"]');
        if (dateErrorClick) {
            this.#handleEditDates();
            return;
        }
    }

    /**
     * Handle input changes within the footer (sample-order checkbox)
     */
    #handleFooterChange(event) {
        const sampleToggle = event.target.closest('[data-action="toggle-sample-order"]');
        if (!sampleToggle) return;

        window.BooqableConfig?.setSampleOrder?.(sampleToggle.checked);

        // Re-render so the progress bar and the Continue button pick up the
        // new minimum-spend status.
        this.#renderMinValueBar();
        this.#renderSummary();
    }

    /**
     * Handle input changes within cart content
     */
    #handleContentChange(event) {
        const target = event.target;

        if (target.classList.contains('BooqableCart__QuantityInput')) {
            const index = parseInt(target.dataset.index, 10);
            const minQty = parseInt(target.dataset.minQty, 10) || 1;
            let quantity = parseInt(target.value, 10);

            if (isNaN(quantity) || quantity < minQty) {
                quantity = minQty;
            } else {
                // Snap to nearest valid step
                quantity = Math.round(quantity / minQty) * minQty;
                if (quantity < minQty) quantity = minQty;
            }

            target.value = quantity;
            this.#debouncedUpdateQuantity(index, quantity);
        }
    }

    /**
     * Handle quantity button click
     */
    #handleQuantityButton(button) {
        const index = parseInt(button.dataset.index, 10);
        const action = button.dataset.action;
        const input = this.#content.querySelector(
            `.BooqableCart__QuantityInput[data-index="${index}"]`
        );

        if (!input) return;

        const minQty = parseInt(input.dataset.minQty, 10) || 1;
        let quantity = parseInt(input.value, 10);

        if (action === 'increase') {
            quantity += minQty;
        } else if (action === 'decrease' && quantity > minQty) {
            quantity -= minQty;
        }

        input.value = quantity;
        this.#debouncedUpdateQuantity(index, quantity);
    }

    /**
     * Observe cart changes from SDK
     */
    #observeCartChanges() {
        // SDK events
        if (window.Booqable?.on) {
            window.Booqable.on('cart:updated', async () => {
                if (this.#isRecentApiUpdate()) return;

                this.#cartData = window.Booqable.cartData;
                await this.#fetchFullCartData();
                this.#render();
                this.#updateHeaderCount();
            });
        }

        // Polling fallback
        this.#pollInterval = setInterval(async () => {
            if (this.#isRecentApiUpdate()) return;

            const newData = window.Booqable?.cartData;
            if (newData && JSON.stringify(newData) !== JSON.stringify(this.#cartData)) {
                this.#cartData = newData;
                await this.#fetchFullCartData();
                this.#render();
                this.#updateHeaderCount();
            }
        }, POLL_INTERVAL);
    }

    // ============================================
    // API METHODS
    // ============================================

    /**
     * Fetch full cart data from Booqable API
     */
    async #fetchFullCartData() {
        const cartId = window.Booqable?.cartData?.id ||
            this.#fullCartData?.cart?.id ||
            this.#cartData?.cartId ||
            localStorage.getItem('bqCartId');
        if (!cartId) {
            this.#fullCartData = null;
            return;
        }

        try {
            this.#abortController = new AbortController();

            const provider = window.BooqableConfig?.provider || 'Shopify';
            const url = `${this.#apiBaseUrl}/cart?source=store&provider=${provider}&id=${cartId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: this.#getApiHeaders(),
                signal: this.#abortController.signal
            });

            if (response.ok) {
                this.#fullCartData = await response.json();
                this.#saveCartId();
            } else {
                console.warn('[BooqableCartDrawer] Failed to fetch cart:', response.status);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.warn('[BooqableCartDrawer] Error fetching cart:', error);
            }
        }
    }

    /**
     * Update item quantity via API
     */
    async #updateItemQuantity(index, quantity) {
        const cart = this.#fullCartData?.cart;
        const line = cart?.lines?.[index];

        if (!line || !cart) {
            console.warn('[BooqableCartDrawer] Cannot update: missing cart or line data');
            return;
        }

        try {
            const response = await fetch(`${this.#apiBaseUrl}/cart/book`, {
                method: 'POST',
                headers: this.#getApiHeaders(true),
                body: JSON.stringify({
                    item_id: line.item_id,
                    line_id: line.id,
                    quantity,
                    type: 'set',
                    id: cart.id
                })
            });

            if (response.ok) {
                this.#fullCartData = await response.json();
                this.#saveCartId();
                this.#markApiUpdate();
                this.#render();
                this.#updateHeaderCount();
                this.#dispatchEvent('cart:updated', { cart: this.#fullCartData.cart });
            } else {
                console.error('[BooqableCartDrawer] Failed to update quantity:', response.status);
            }
        } catch (error) {
            console.error('[BooqableCartDrawer] Error updating quantity:', error);
        }
    }

    /**
     * Remove item from cart via API
     */
    async #removeItem(index) {
        const cart = this.#fullCartData?.cart;
        const line = cart?.lines?.[index];

        if (!line || !cart) {
            console.warn('[BooqableCartDrawer] Cannot remove: missing cart or line data');
            return;
        }

        try {
            const response = await fetch(`${this.#apiBaseUrl}/cart/book`, {
                method: 'POST',
                headers: this.#getApiHeaders(true),
                body: JSON.stringify({
                    item_id: line.item_id,
                    line_id: line.id,
                    quantity: 0,
                    type: 'set',
                    id: cart.id
                })
            });

            if (response.ok) {
                this.#fullCartData = await response.json();
                this.#saveCartId();
                this.#markApiUpdate();
                this.#render();
                this.#updateHeaderCount();
                this.#dispatchEvent('cart:updated', { cart: this.#fullCartData.cart });
            } else {
                console.error('[BooqableCartDrawer] Failed to remove item:', response.status);
            }
        } catch (error) {
            console.error('[BooqableCartDrawer] Error removing item:', error);
        }
    }

    /**
     * Handle edit dates button click
     * Opens the date picker modal with current cart dates
     */
    #handleEditDates() {
        console.log('[BooqableCartDrawer] Edit dates clicked');

        const datePicker = document.querySelector('booqable-date-picker');
        if (!datePicker) {
            console.warn('[BooqableCartDrawer] Date picker not found');
            return;
        }

        // Get current cart dates
        const cart = this.#fullCartData?.cart;
        const startDate = cart?.starts_at ? new Date(cart.starts_at) : null;
        const endDate = cart?.stops_at ? new Date(cart.stops_at) : null;

        // Open date picker - the date picker handles the API update directly
        datePicker.open(startDate, endDate);
    }

    /**
     * Get API headers
     */
    #getApiHeaders(includeContentType = false) {
        const headers = {
            'Accept': 'application/json',
            'x-booqable-store-provider': window.BooqableConfig?.provider || 'Shopify',
            'x-booqable-storefront-token': this.#storefrontToken
        };

        if (includeContentType) {
            headers['Content-Type'] = 'application/json';
        }

        return headers;
    }

    // ============================================
    // RENDERING
    // ============================================

    /**
     * Render cart contents
     */
    #render() {
        const cart = this.#fullCartData?.cart;
        const lines = cart?.lines || [];
        const hasItems = lines.length > 0 || (this.#cartData?.items?.length > 0);

        this.#renderMinValueBar();

        if (!hasItems) {
            this.#renderEmptyState();
            return;
        }

        this.#renderCartItems(lines);
        this.#renderSummary();
        this.#footer.style.display = 'block';
    }

    /**
     * Render empty state
     */
    #renderEmptyState() {
        this.#content.innerHTML = `
      <div class="BooqableCart__Empty">
        <p class="BooqableCart__EmptyText">Your rental cart is empty</p>
        <a href="/collections/all" class="Button Button--primary">Continue Browsing</a>
      </div>
    `;
        this.#footer.style.display = 'none';
    }

    /**
     * Render cart items
     */
    #renderCartItems(lines) {
        const cart = this.#fullCartData?.cart;

        // Render rental dates once at the top
        const startDate = cart?.starts_at ? this.#formatDate(cart.starts_at) : null;
        const endDate = cart?.stops_at ? this.#formatDate(cart.stops_at) : null;
        const datesHtml = (startDate && endDate) ? `
          <div class="BooqableCart__RentalPeriod">
            <button type="button" class="BooqableCart__EditDatesBtn" data-action="edit-dates" aria-label="Edit rental dates">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M11.5 2.5l2 2M2 14l1-4 9-9 2 2-9 9-4 1z"/>
              </svg>
              Edit
            </button>
            <span class="BooqableCart__RentalPeriodLabel">Rental Period:</span>
            <div class="BooqableCart__RentalDates">
              <span><strong>From:</strong> ${startDate}</span>
              <span class="BooqableCart__DateSeparator">→</span>
              <span><strong>To:</strong> ${endDate}</span>
            </div>
          </div>
        ` : '';

        if (lines.length > 0) {
            this.#content.innerHTML = `
              ${datesHtml}
              <div class="BooqableCart__ItemList">
                ${lines.map((line, index) => this.#renderLineItem(line, index, cart)).join('')}
              </div>
            `;
        } else {
            // Fallback to SDK data
            const items = this.#cartData?.items || [];
            this.#content.innerHTML = `
              ${datesHtml}
              <div class="BooqableCart__ItemList">
                ${items.map((item, index) => this.#renderBasicItem(item, index)).join('')}
              </div>
            `;
        }
    }

    /**
     * Render line item from API data
     */
    #renderLineItem(line, index, cart) {
        const name = this.#escapeHtml(line.title || 'Product');
        const imageUrl = line.photo_url || line.product?.photo_url || '';
        const price = this.#formatPriceCents(line.price_in_cents || 0);
        const quantity = line.quantity || 1;
        const minQty = this.#getMinOrderQuantity(line.item_id);

        return `
      <div class="BooqableCart__Item" data-item-index="${index}" data-line-id="${line.id}" data-item-id="${line.item_id}">
        <div class="BooqableCart__ItemImage">
          ${imageUrl
                ? `<img src="${imageUrl}" alt="${name}" loading="lazy">`
                : '<span class="BooqableCart__ImagePlaceholder"></span>'}
        </div>
        <div class="BooqableCart__ItemInfo">
          <h4 class="BooqableCart__ItemName">${name}</h4>
          <div class="BooqableCart__ItemMeta">
            <span class="BooqableCart__ItemPrice">${price}</span>
          </div>
          <div class="BooqableCart__ItemActions">
            ${this.#renderQuantitySelector(index, quantity, minQty)}
            <button type="button" class="BooqableCart__RemoveBtn" data-action="remove" data-index="${index}">Remove</button>
          </div>
        </div>
      </div>
    `;
    }

    /**
     * Render basic item from SDK data (fallback)
     */
    #renderBasicItem(item, index) {
        const name = this.#escapeHtml(item.item_name || 'Product');
        const price = this.#formatPrice(item.price);
        const quantity = item.quantity || 1;
        const minQty = this.#getMinOrderQuantity(item.item_id);

        return `
      <div class="BooqableCart__Item" data-item-index="${index}" data-item-id="${item.item_id}">
        <div class="BooqableCart__ItemImage">
          <span class="BooqableCart__ImagePlaceholder"></span>
        </div>
        <div class="BooqableCart__ItemInfo">
          <h4 class="BooqableCart__ItemName">${name}</h4>
          <div class="BooqableCart__ItemMeta">
            <span class="BooqableCart__ItemPrice">${price}</span>
          </div>
          <div class="BooqableCart__ItemActions">
            ${this.#renderQuantitySelector(index, quantity, minQty)}
            <button type="button" class="BooqableCart__RemoveBtn" data-action="remove" data-index="${index}">Remove</button>
          </div>
        </div>
      </div>
    `;
    }

    /**
     * Render quantity selector
     */
    #renderQuantitySelector(index, quantity, minQty = 1) {
        return `
      <div class="BooqableCart__QuantitySelector">
        <button type="button" class="BooqableCart__QuantityBtn" data-action="decrease" data-index="${index}" aria-label="Decrease quantity">
          <svg viewBox="0 0 16 2"><path d="M1,1 L15,1" stroke="currentColor" fill="none" stroke-linecap="square"></path></svg>
        </button>
        <input type="number" class="BooqableCart__QuantityInput" value="${quantity}" min="${minQty}" step="${minQty}" data-index="${index}" data-min-qty="${minQty}" aria-label="Quantity">
        <button type="button" class="BooqableCart__QuantityBtn" data-action="increase" data-index="${index}" aria-label="Increase quantity">
          <svg viewBox="0 0 16 16"><g stroke="currentColor" fill="none" stroke-linecap="square"><path d="M8,1 L8,15"></path><path d="M1,8 L15,8"></path></g></svg>
        </button>
      </div>
    `;
    }

    /**
     * Render cart summary
     */
    #renderSummary() {
        const cart = this.#fullCartData?.cart;
        const currency = this.#fullCartData?.meta?.currency;

        let subtotal, deposit, total, itemCount;

        if (cart) {
            subtotal = cart.grand_total_in_cents || 0;
            deposit = cart.deposit_in_cents || 0;
            total = cart.grand_total_with_tax_in_cents || 0;
            itemCount = cart.lines?.length || 0;
        } else {
            subtotal = (this.#cartData?.grandtotal || 0) * 100;
            deposit = 0;
            total = (this.#cartData?.grandTotalWithTax || 0) * 100;
            itemCount = this.#cartData?.items?.length || 0;
        }

        // Check if rental date is valid (must be after today)
        const dateError = this.#getDateValidationError();
        // Cart subtotal threshold check (progress bar itself is rendered at top of drawer)
        const progress = this.#getCartValueProgress();
        // Below-minimum carts can still Continue, but the button shows the
        // "get in touch" prompt instead of navigating. Only date errors disable it.
        const minNotMet = progress && !progress.met && !dateError;

        this.#footer.innerHTML = `
      <div class="BooqableCart__Summary">
        <div class="BooqableCart__SummaryRow">
          <span class="BooqableCart__SummaryLabel">Subtotal (${itemCount} items)</span>
          <span class="BooqableCart__SummaryValue">${this.#formatPriceCents(subtotal, currency)}</span>
        </div>
        <div class="BooqableCart__SummaryRow">
          <span class="BooqableCart__SummaryLabel">Loss & Breakages Deposit (Refundable)</span>
          <span class="BooqableCart__SummaryValue">${this.#formatPriceCents(deposit, currency)}</span>
        </div>
        <div class="BooqableCart__SummaryRow BooqableCart__Total">
          <span class="BooqableCart__SummaryLabel">Total</span>
          <span class="BooqableCart__SummaryValue">${this.#formatPriceCents(total, currency)}</span>
        </div>
        ${dateError ? `
        <div class="BooqableCart__DateError" data-action="open-date-picker" style="cursor: pointer;" title="Click to select dates">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <span>${dateError} <u>Click here to select</u></span>
        </div>
        ` : ''}
      </div>
      ${this.#renderSampleOrderToggle()}
      <a href="/pages/rent-cart" class="BooqableCart__CheckoutBtn ${dateError ? 'is-disabled' : ''}"${minNotMet ? ' data-min-not-met="true"' : ''}>CONTINUE</a>
    `;
    }

    /**
     * Sample-order opt-in shown above the Continue button.
     * Ticking it exempts the cart from the minimum spend.
     * Hidden when no minimum is configured, since there is nothing to override.
     */
    #renderSampleOrderToggle() {
        const minPounds = parseFloat(window.BooqableConfig?.minCartValue) || 0;
        if (minPounds <= 0) return '';

        const checked = window.BooqableConfig?.isSampleOrder?.() ? ' checked' : '';
        const label = this.#escapeHtml(
            window.BooqableConfig?.sampleOrderLabel || 'I confirm this order is for samples'
        );

        return `
      <label class="BooqableCart__SampleOrder">
        <input type="checkbox" class="BooqableCart__SampleOrderInput" data-action="toggle-sample-order"${checked}>
        <span class="BooqableCart__SampleOrderLabel">${label}</span>
      </label>
    `;
    }

    /**
     * Check if rental dates are valid
     * Returns error message if invalid, null if valid
     */
    #getDateValidationError() {
        const cart = this.#fullCartData?.cart;

        // Check if dates are missing
        if (!cart?.starts_at || !cart?.stops_at) {
            return 'Please select rental dates to continue.';
        }

        const startDate = new Date(cart.starts_at);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Start date must be after today (tomorrow or later)
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (startDate < tomorrow) {
            return 'The period you selected is not available.';
        }

        return null;
    }

    /**
     * Compute progress toward the minimum order value
     * Returns null when the rule is inactive (no minimum, empty cart, or totals not loaded)
     * Otherwise returns { met, percent, missingCents, minCents, currency }
     */
    #getCartValueProgress() {
        // Threshold logic lives in BooqableConfig so the drawer and the
        // checkout summary/form enforce an identical minimum.
        const status = window.BooqableConfig?.getMinSpendStatus?.(this.#fullCartData?.cart);
        if (!status) return null;

        const percent = status.sample
            ? 100
            : Math.min(100, Math.round((status.subtotalCents / status.minCents) * 100));
        const currency = this.#fullCartData?.meta?.currency;

        return {
            met: status.met,
            sample: status.sample,
            percent,
            missingCents: status.missingCents,
            minCents: status.minCents,
            currency
        };
    }

    /**
     * Substitute {missing} and {minimum} placeholders in a merchant message template
     * Escapes the template first so merchant input cannot inject HTML
     */
    #formatMinValueMessage(template, progress) {
        const safe = this.#escapeHtml(template || '');
        const missing = this.#formatPriceCents(progress.missingCents, progress.currency);
        const minimum = this.#formatPriceCents(progress.minCents, progress.currency);
        return safe
            .replace(/\{missing\}/g, missing)
            .replace(/\{minimum\}/g, minimum);
    }

    /**
     * Render the minimum-order progress bar at the top of the drawer
     * Hides the slot if the rule is inactive
     */
    #renderMinValueBar() {
        if (!this.#minValueSlot) return;

        const progress = this.#getCartValueProgress();

        if (!progress) {
            this.#minValueSlot.innerHTML = '';
            this.#minValueSlot.style.display = 'none';
            return;
        }

        let template;
        if (progress.sample) {
            template = window.BooqableConfig?.minCartMessageSample || 'Sample order - no minimum spend applies';
        } else if (progress.met) {
            template = window.BooqableConfig?.minCartMessageMet || '✓ Minimum order met';
        } else {
            template = window.BooqableConfig?.minCartMessageBelow || 'Add {missing} more to reach the {minimum} minimum';
        }
        const message = this.#formatMinValueMessage(template, progress);

        const linkLabel = window.BooqableConfig?.minCartModalLinkLabel || 'Unable to meet the min spend?';
        const linkHtml = progress.met ? '' : `
        <button type="button" class="BooqableCart__MinValueLink" data-action="open-minspend-modal">${this.#escapeHtml(linkLabel)}</button>`;

        this.#minValueSlot.innerHTML = `
      <div class="BooqableCart__MinValue ${progress.met ? 'is-met' : ''} ${progress.sample ? 'is-sample' : ''}">
        <div class="BooqableCart__MinValueBar">
          <div class="BooqableCart__MinValueBarFill" style="width: ${progress.percent}%;"></div>
        </div>
        <div class="BooqableCart__MinValueText">${message}</div>${linkHtml}
      </div>
    `;
        this.#minValueSlot.style.display = '';
    }

    /**
     * Update cart count in header
     */
    #updateHeaderCount() {
        const countEl = document.querySelector('.Header__BooqableCartCount');
        if (!countEl) return;

        const lines = this.#fullCartData?.cart?.lines;
        const count = lines?.length ?? this.#cartData?.items?.length ?? 0;

        countEl.textContent = count;
        countEl.classList.toggle('is-visible', count > 0);
    }

    // ============================================
    // PUBLIC METHODS
    // ============================================

    /**
     * Open the drawer
     */
    open() {
        this.#drawer.classList.add('is-open');
        this.#overlay?.classList.add('is-visible');
        document.body.style.overflow = 'hidden';
        this.#drawer.setAttribute('aria-hidden', 'false');
        this.#dispatchEvent('cart:open');
    }

    /**
     * Close the drawer
     */
    close() {
        this.#drawer.classList.remove('is-open');
        this.#overlay?.classList.remove('is-visible');
        document.body.style.overflow = '';
        this.#drawer.setAttribute('aria-hidden', 'true');
        this.#dispatchEvent('cart:close');
    }

    /**
     * Check if drawer is open
     */
    get isOpen() {
        return this.#drawer.classList.contains('is-open');
    }

    /**
     * Public: open the below-minimum prompt.
     * Lets sibling components (e.g. the checkout form) reuse the same modal.
     */
    openMinSpendModal() {
        this.#openMinSpendModal();
    }

    /**
     * Open the below-minimum prompt
     */
    #openMinSpendModal() {
        if (!this.#minSpendModal) return;
        this.#minSpendModal.classList.add('is-open');
        this.#minSpendModal.setAttribute('aria-hidden', 'false');
    }

    /**
     * Close the below-minimum prompt
     */
    #closeMinSpendModal() {
        if (!this.#minSpendModal) return;
        this.#minSpendModal.classList.remove('is-open');
        this.#minSpendModal.setAttribute('aria-hidden', 'true');
    }

    /**
     * Whether the below-minimum prompt is currently open
     */
    get #isMinSpendModalOpen() {
        return this.#minSpendModal?.classList.contains('is-open') ?? false;
    }

    /**
     * Get current cart data
     */
    get cart() {
        return this.#fullCartData?.cart || null;
    }

    /**
     * Get full cart data (for date picker and other components)
     */
    get fullCartData() {
        return this.#fullCartData;
    }

    /**
     * Refresh cart data
     */
    async refresh() {
        await this.#fetchFullCartData();
        this.#render();
        this.#updateHeaderCount();
    }

    /**
     * Get cart data asynchronously (waits for initialization)
     */
    async getCartDataAsync() {
        if (this.#fullCartData) return this.#fullCartData;

        // If not initialized, ensure initialization starts/completes
        if (!this.#isInitialized && this.isConnected) {
            // Wait a bit for connection
        }

        return new Promise((resolve) => {
            const startTime = Date.now();
            const check = () => {
                if (this.#fullCartData) {
                    resolve(this.#fullCartData);
                    return;
                }

                // Timeout after 5 seconds
                if (Date.now() - startTime > 5000) {
                    console.warn('[BooqableCartDrawer] getCartDataAsync timed out');
                    resolve(null);
                    return;
                }

                requestAnimationFrame(check);
            };
            check();
        });
    }

    // ============================================
    // UTILITIES
    // ============================================

    /**
     * Get min order quantity for a Booqable product from localStorage
     */
    #getMinOrderQuantity(itemId) {
        try {
            var stored = JSON.parse(localStorage.getItem('bqMinOrderQty') || '{}');
            return stored[itemId] || 1;
        } catch (e) {
            return 1;
        }
    }

    /**
     * Debounced quantity update
     */
    #debouncedUpdateQuantity(index, quantity) {
        const key = `qty-${index}`;

        if (this.#debounceTimers.has(key)) {
            clearTimeout(this.#debounceTimers.get(key));
        }

        const timer = setTimeout(() => {
            this.#debounceTimers.delete(key);
            this.#updateItemQuantity(index, quantity);
        }, DEBOUNCE_DELAY);

        this.#debounceTimers.set(key, timer);
    }

    /**
     * Mark API update timestamp
     */
    #markApiUpdate() {
        this.#lastApiUpdate = Date.now();
    }

    /**
     * Save cart ID to localStorage for persistence
     */
    #saveCartId() {
        const cartId = this.#fullCartData?.cart?.id;
        if (cartId) {
            localStorage.setItem('bqCartId', cartId);
            console.log('[BooqableCartDrawer] Cart ID saved to localStorage:', cartId);
        }
    }

    /**
     * Check if there was a recent API update
     */
    #isRecentApiUpdate() {
        return this.#lastApiUpdate && (Date.now() - this.#lastApiUpdate) < API_UPDATE_GRACE_PERIOD;
    }

    /**
     * Dispatch custom event
     */
    #dispatchEvent(name, detail = {}) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            composed: true,
            detail
        }));
    }

    /**
     * Format price from cents
     */
    #formatPriceCents(cents, currency = null) {
        if (typeof cents !== 'number') cents = 0;
        const precision = currency?.precision ?? 2;
        const symbol = currency?.symbol ?? '£';
        const amount = (cents / 100).toFixed(precision);
        return `${symbol}${amount}`;
    }

    /**
     * Format price (main currency units)
     */
    #formatPrice(price, currencyCode = 'GBP') {
        if (typeof price !== 'number') price = 0;
        const symbol = CURRENCY_SYMBOLS[currencyCode] ?? '£';
        return `${symbol}${price.toFixed(2)}`;
    }

    /**
     * Format date for display (date only, no time)
     */
    #formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Escape HTML to prevent XSS
     */
    #escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// Register the custom element
customElements.define('booqable-cart-drawer', BooqableCartDrawer);

// Export for external use
window.BooqableCartDrawer = BooqableCartDrawer;
